/*
  #241 (client follow-up): mouse users can't swipe a desktop carousel and a
  mechanical mouse has no horizontal scroll. Click-and-drag on the track now
  scrolls it (touch and trackpad keep their native behaviour).

  Release: native scroll-snap would jump the track the instant it is turned
  back on, so it stays off while the track glides on its own momentum and
  eases out onto the slide nearest to where that glide would end. Snap is
  restored only once the track sits exactly on that slide, so nothing jumps.
  The click that ends a drag is swallowed so a card doesn't open.
*/
(() => {
  if (window.csoSliderDragInit) return;
  window.csoSliderDragInit = true;

  const DRAG_THRESHOLD = 5; // px before a press becomes a drag
  const MOMENTUM_MS = 220; // how far the release velocity projects the glide
  const SETTLE_MIN_MS = 320;
  const SETTLE_MAX_MS = 720;
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  // The position native snap would pick for each slide: offsetLeft minus the
  // slide's scroll-margin and the track's scroll-padding.
  const snapPoints = (track) => {
    const component = track.closest('slider-component');
    const items =
      component && component.sliderItemsToShow && component.sliderItemsToShow.length
        ? component.sliderItemsToShow
        : track.querySelectorAll('.slider__slide');
    const padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    const max = track.scrollWidth - track.clientWidth;
    const points = [];
    items.forEach((item) => {
      const margin = parseFloat(getComputedStyle(item).scrollMarginLeft) || 0;
      points.push(Math.min(max, Math.max(0, item.offsetLeft - margin - padding)));
    });
    return points;
  };

  const nearest = (points, position) =>
    points.reduce((best, p) => (Math.abs(p - position) < Math.abs(best - position) ? p : best), points[0]);

  const init = (track) => {
    if (track.dataset.dragScroll) return;
    track.dataset.dragScroll = 'true';

    const component = track.closest('slider-component');
    let pointerId = null;
    let startX = 0;
    let startLeft = 0;
    let moved = false;
    let lastDragEnd = 0;
    let samples = [];
    let settleFrame = 0;

    const stopSettle = () => {
      if (settleFrame) cancelAnimationFrame(settleFrame);
      settleFrame = 0;
      track.classList.remove('is-settling');
    };

    const settle = (velocity) => {
      const points = snapPoints(track);
      if (!points.length) return;
      const from = track.scrollLeft;
      const projected = from + velocity * MOMENTUM_MS;
      const target = nearest(points, projected);
      const distance = Math.abs(target - from);
      if (distance < 1) {
        stopSettle();
        return;
      }
      const duration = Math.min(SETTLE_MAX_MS, Math.max(SETTLE_MIN_MS, distance * 0.9));
      track.classList.add('is-settling');
      const startTime = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - startTime) / duration);
        track.scrollLeft = from + (target - from) * easeOutCubic(t);
        if (t < 1) {
          settleFrame = requestAnimationFrame(step);
          return;
        }
        track.scrollLeft = target;
        stopSettle();
        if (component && typeof component.update === 'function') component.update();
      };
      settleFrame = requestAnimationFrame(step);
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      stopSettle();
      pointerId = event.pointerId;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      moved = false;
      samples = [{ t: performance.now(), x: event.clientX }];
    });

    track.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        moved = true;
        track.classList.add('is-dragging');
        try {
          track.setPointerCapture(pointerId);
        } catch (e) {
          /* capture is best effort */
        }
      }
      track.scrollLeft = startLeft - dx;
      const now = performance.now();
      samples.push({ t: now, x: event.clientX });
      samples = samples.filter((s) => now - s.t <= 100);
      event.preventDefault();
    });

    const end = (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      pointerId = null;
      if (!moved) return;
      moved = false;
      lastDragEnd = Date.now();
      // Velocity in scroll px per ms (dragging left moves the track right).
      const now = performance.now();
      const first = samples[0];
      const last = { t: now, x: event.clientX };
      let velocity = 0;
      if (first && last.t - first.t > 0) velocity = -(last.x - first.x) / (last.t - first.t);
      // Keep snap off (is-settling) while the glide runs; is-dragging only
      // carries the grabbing cursor and text-selection lock.
      track.classList.add('is-settling');
      track.classList.remove('is-dragging');
      settle(velocity);
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);

    // Native image/link drag would hijack the gesture.
    track.addEventListener('dragstart', (event) => {
      if (pointerId !== null || track.classList.contains('is-dragging')) event.preventDefault();
    });

    // The click released at the end of a drag must not open a product.
    track.addEventListener(
      'click',
      (event) => {
        if (Date.now() - lastDragEnd < 300) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  };

  const boot = () => document.querySelectorAll('slider-component .slider--desktop').forEach(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
